/** pt-BR formatting helpers (display only; identifiers stay in English). */

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const numberFormatter = new Intl.NumberFormat('pt-BR');

export const formatCurrency = (value: number | undefined | null): string =>
  currencyFormatter.format(Number(value ?? 0));

export const formatNumber = (value: number | undefined | null): string =>
  numberFormatter.format(Number(value ?? 0));

export const formatPercent = (value: number | undefined | null, fractionDigits = 1): string =>
  `${Number(value ?? 0).toFixed(fractionDigits)}%`;

export const formatDate = (iso: string | undefined | null): string => {
  if (!iso) return '-';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

export const formatDateTime = (iso: string | undefined | null): string => {
  if (!iso) return '-';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Brazilian phone number, e.g. "(11) 9 8888-7777" (landline: "(11) 3888-7777").
 * Input is free-text (no mask enforced on cadastro), so it may or may not
 * carry the "+55" country code, spaces, dashes, parentheses, etc. - strip
 * everything down to digits, drop a leading "55" DDI when present, then
 * format by the remaining length (11 = celular, 10 = fixo).
 */
export const formatPhone = (value: string | undefined | null): string => {
  if (!value) return '-';
  let digits = value.replace(/\D/g, '');
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    digits = digits.slice(2);
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return value;
};

/** Signed growth label, e.g. "+12,5%" / "-3,2%". */
export const formatGrowth = (value: number | undefined | null): string => {
  const n = Number(value ?? 0);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};

/**
 * Tamanho de arquivo em pt-BR, e.g. "2,4 MB".
 *
 * Existe para a lista de anexos: "2412544" não diz a ninguém se o contrato
 * social cabe num anexo de e-mail, e é essa a pergunta de quem olha a linha.
 */
export const formatBytes = (value: number | undefined | null): string => {
  const bytes = Number(value ?? 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const scaled = bytes / 1024 ** exponent;
  // Bytes inteiros não ganham casa decimal - "512,0 B" é ruído.
  const digits = exponent === 0 ? 0 : 1;
  return `${scaled.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${units[exponent]}`;
};
